import { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@components/button";
import ImageInput from "@components/imageInput";
import TextInput from "@components/textInput";
import styles from "./AdminPubblicita.module.css";
import Select from "@components/select";
import SelectCategory from "@templates/selectCategory";
import SelectUser from "@templates/selectUser";
import { useSnackbars } from "@contexts/SnackbarsContext";
import * as apiAdministration from "@api/administration";
import { getSponsorImageUrl } from "@logic/sponsor";
import { useCategories } from "@contexts/CategoriesContext";

const fromFileInputToBlobPromise = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      resolve(event.target.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

const MODES = {
  SEAMLESS: "seamless",
  BANNER: "banner",
};

const DESTINATIONS = {
  LINK: "link",
  IMAGE: "image",
};

const DEFAULT_FORM = {
  id: null,
  mode: MODES.SEAMLESS,
  userId: "",
  expirationDate: "",
  categoryIds: [],
  destinationType: DESTINATIONS.LINK,
  destinationLink: "",
  destinationImage: "",
  seamlessTitle: "",
  seamlessText: "",
  seamlessImage: "",
  bannerImage: "",
};

const AdminPubblicita = () => {
  const { openSnackbar } = useSnackbars();
  const { getCategoryDescriptionById } = useCategories();
  const [formState, setFormState] = useState(DEFAULT_FORM);
  const [advertisements, setAdvertisements] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seamlessImages, setSeamlessImages] = useState([]);
  const [bannerImages, setBannerImages] = useState([]);
  const [destinationImages, setDestinationImages] = useState([]);
  const [destinationImageTouched, setDestinationImageTouched] = useState(false);

  const fetchSponsorImageFile = useCallback(async (advertisement, imageName) => {
    const url = getSponsorImageUrl({
      userId: advertisement.userId,
      sponsorId: advertisement.id,
      imageName,
    });
    if (!url) {
      throw new Error("Immagine sponsor non disponibile");
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error("Immagine sponsor non disponibile");
    }
    const blob = await response.blob();
    return new File([blob], `${imageName}.jpg`, { type: blob.type || "image/jpeg" });
  }, []);

  const preloadSponsorImages = useCallback(async (advertisement) => {
    const availableImages = advertisement?.images || [];
    const preloadImage = async (imageName, setImages, onLoaded) => {
      if (!availableImages.includes(imageName)) {
        setImages([]);
        return;
      }
      try {
        const file = await fetchSponsorImageFile(advertisement, imageName);
        onLoaded?.();
        setImages([file]);
      } catch (error) {
        openSnackbar(`❌ ${error.message}`);
        setImages([]);
      }
    };

    const tasks = [];
    if (advertisement?.sponsorType === 2) {
      tasks.push(preloadImage("square", setSeamlessImages));
    } else {
      tasks.push(preloadImage("small", setBannerImages));
    }

    if (advertisement?.sponsorDestinationType === 2) {
      tasks.push(
        preloadImage("destination", setDestinationImages, () => setDestinationImageTouched(true))
      );
    }

    await Promise.all(tasks);
  }, [fetchSponsorImageFile, openSnackbar]);

  const loadSponsors = useCallback(() => {
    setLoading(true);
    apiAdministration
      .GetSponsors()
      .then((response) => setAdvertisements(response || []))
      .catch((error) => openSnackbar(`❌ ${error.message}`))
      .finally(() => setLoading(false));
  }, [openSnackbar]);

  useEffect(() => {
    loadSponsors();
  }, [loadSponsors]);

  const categoryIds = useMemo(() => (
    (formState.categoryIds || []).map((item) => Number(item)).filter((value) => !Number.isNaN(value))
  ), [formState.categoryIds]);

  const onChange = useCallback((key, value) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState(DEFAULT_FORM);
    setSeamlessImages([]);
    setBannerImages([]);
    setDestinationImages([]);
    setDestinationImageTouched(false);
  }, []);

  const validateForm = useCallback(() => {
    if (!formState.userId) {
      throw new Error("Inserisci l'ID dell'utente business associato");
    }

    if (!Number.isInteger(Number(formState.userId))) {
      throw new Error("Inserisci un ID utente valido");
    }

    if (!formState.expirationDate) {
      throw new Error("Imposta una data di scadenza");
    }

    if (formState.mode === MODES.SEAMLESS) {
      if (!formState.seamlessImage) {
        throw new Error("L'immagine quadrata è obbligatoria per gli annunci seamless");
      }
      if (!formState.seamlessTitle || !formState.seamlessText) {
        throw new Error("Titolo e testo sono obbligatori per gli annunci seamless");
      }
    } else {
      if (!formState.bannerImage) {
        throw new Error("L'immagine 382x118 è obbligatoria per i banner");
      }
    }

    if (formState.destinationType === DESTINATIONS.LINK && !formState.destinationLink) {
      throw new Error("Inserisci il link di destinazione");
    }

    if (formState.destinationType === DESTINATIONS.IMAGE && !formState.destinationImage) {
      throw new Error("Carica l'immagine di destinazione");
    }
  }, [formState]);

  const mapDestinationUrl = useCallback(() => {
    if (formState.destinationType === DESTINATIONS.LINK) {
      return formState.destinationLink;
    }
    return "";
  }, [formState.destinationLink, formState.destinationType]);

  const onSave = useCallback(() => {
    try {
      validateForm();
    } catch (error) {
      openSnackbar(`❌ ${error.message}`);
      return;
    }

    setSaving(true);

    const payload = {
      userId: Number(formState.userId),
      sponsorType: formState.mode === MODES.SEAMLESS ? 2 : 1,
      sponsorDestinationType: formState.destinationType === DESTINATIONS.LINK ? 1 : 2,
      endDate: formState.expirationDate,
      destinationUrl: mapDestinationUrl(),
      title: formState.seamlessTitle,
      description: formState.seamlessText,
      categoryIds,
      imageSquare: formState.seamlessImage,
      imageSmall: formState.bannerImage,
      imageDestination: formState.destinationImage,
    };

    const action = formState.id
      ? apiAdministration.EditSponsor({ ...payload, sponsorId: formState.id })
      : apiAdministration.CreateSponsor(payload);

    action
      .then(() => {
        openSnackbar("✅ Pubblicità salvata");
        resetForm();
        loadSponsors();
      })
      .catch((error) => openSnackbar(`❌ ${error.message}`))
      .finally(() => setSaving(false));
  }, [
    formState.bannerImage,
    formState.destinationImage,
    formState.destinationType,
    formState.expirationDate,
    formState.id,
    formState.mode,
    formState.seamlessImage,
    formState.seamlessText,
    formState.seamlessTitle,
    formState.userId,
    categoryIds,
    loadSponsors,
    mapDestinationUrl,
    openSnackbar,
    resetForm,
    validateForm,
  ]);

  const onDelete = useCallback(
    (id) => {
      setSaving(true);
      apiAdministration
        .DeleteSponsor({ sponsorId: id })
        .then(() => {
          openSnackbar("✅ Pubblicità eliminata");
          if (formState.id === id) {
            resetForm();
          }
          loadSponsors();
        })
        .catch((error) => openSnackbar(`❌ ${error.message}`))
        .finally(() => setSaving(false));
    },
    [formState.id, loadSponsors, openSnackbar, resetForm]
  );

  const onEdit = useCallback((advertisement) => {
    setFormState({
      id: advertisement.id,
      mode: advertisement.sponsorType === 2 ? MODES.SEAMLESS : MODES.BANNER,
      userId: advertisement.userId?.toString() || "",
      expirationDate: advertisement.endDate?.slice(0, 10) || "",
      categoryIds: (advertisement.sponsorCategories || []).map((item) => item.categoryId),
      destinationType:
        advertisement.sponsorDestinationType === 2 ? DESTINATIONS.IMAGE : DESTINATIONS.LINK,
      destinationLink: advertisement.destinationUrl || "",
      destinationImage: "",
      seamlessTitle: advertisement.title || "",
      seamlessText: advertisement.description || "",
      seamlessImage: "",
      bannerImage: "",
    });
    setSeamlessImages([]);
    setBannerImages([]);
    setDestinationImages([]);
    setDestinationImageTouched(false);
    preloadSponsorImages(advertisement);
  }, [preloadSponsorImages]);

  const onDestinationImagesChange = useCallback((images) => {
    setDestinationImageTouched(true);
    setDestinationImages(images);
  }, []);

  const onAddCategory = useCallback((categoryId) => {
    if (!categoryId) {
      return;
    }
    setFormState((prev) => {
      const nextIds = new Set(prev.categoryIds || []);
      nextIds.add(Number(categoryId));
      return {
        ...prev,
        categoryIds: Array.from(nextIds),
      };
    });
  }, []);

  const onRemoveCategory = useCallback((categoryId) => {
    setFormState((prev) => ({
      ...prev,
      categoryIds: (prev.categoryIds || []).filter((id) => String(id) !== String(categoryId)),
    }));
  }, []);

  useEffect(() => {
    if (seamlessImages.length === 0) {
      onChange("seamlessImage", "");
      return;
    }

    fromFileInputToBlobPromise(seamlessImages[0])
      .then((value) => onChange("seamlessImage", value))
      .catch((error) => openSnackbar(`❌ ${error.message}`));
  }, [onChange, openSnackbar, seamlessImages]);

  useEffect(() => {
    if (bannerImages.length === 0) {
      onChange("bannerImage", "");
      return;
    }

    fromFileInputToBlobPromise(bannerImages[0])
      .then((value) => onChange("bannerImage", value))
      .catch((error) => openSnackbar(`❌ ${error.message}`));
  }, [bannerImages, onChange, openSnackbar]);

  useEffect(() => {
    if (!destinationImageTouched) {
      return;
    }

    if (destinationImages.length === 0) {
      onChange("destinationImage", "");
      return;
    }

    fromFileInputToBlobPromise(destinationImages[0])
      .then((value) => onChange("destinationImage", value))
      .catch((error) => openSnackbar(`❌ ${error.message}`));
  }, [destinationImageTouched, destinationImages, onChange, openSnackbar]);

  return (
    <>
      <div className="row">
        <div className="col">
          <span className="page-title">Gestione pubblicità</span>
          <p className={styles.subtitle}>Crea o modifica annunci pubblicitari in modalità seamless o banner.</p>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className={styles.card}>
            <div className={styles.splitFields}>
              <Select
                label="Modalità"
                options={[
                  { value: MODES.SEAMLESS, description: "Seamless" },
                  { value: MODES.BANNER, description: "Banner" },
                ]}
                value={formState.mode}
                setValue={(value) => onChange("mode", value || MODES.SEAMLESS)}
                clearable={false}
              />
              <SelectUser
                label="Utente business"
                value={formState.userId}
                setValue={(value) => onChange("userId", value)}
                disabled={saving || loading}
              />
              <TextInput
                label="Data di scadenza"
                value={formState.expirationDate}
                setValue={(value) => onChange("expirationDate", value)}
                type="date"
              />
            </div>

            <div className={styles.categoryField}>
              <SelectCategory value={null} setValue={onAddCategory} disabled={saving || loading} />
              {categoryIds.length > 0 && (
                <div className={styles.categoryTags}>
                  {categoryIds.map((categoryId) => (
                    <span className={styles.categoryTag} key={categoryId}>
                      <span>{getCategoryDescriptionById(categoryId) || categoryId}</span>
                      <button
                        type="button"
                        className={styles.categoryRemove}
                        aria-label={`Rimuovi categoria ${getCategoryDescriptionById(categoryId) || categoryId}`}
                        onClick={() => onRemoveCategory(categoryId)}
                        disabled={saving || loading}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className={styles.splitFields}>
              <Select
                label="Destinazione"
                options={[
                  { value: DESTINATIONS.LINK, description: "Apri link" },
                  { value: DESTINATIONS.IMAGE, description: "Mostra immagine" },
                ]}
                value={formState.destinationType}
                setValue={(value) => onChange("destinationType", value || DESTINATIONS.LINK)}
                clearable={false}
              />
              {formState.destinationType === DESTINATIONS.LINK && (
                <TextInput
                  label="Link di destinazione"
                  value={formState.destinationLink}
                  setValue={(value) => onChange("destinationLink", value)}
                />
              )}
              {formState.destinationType === DESTINATIONS.IMAGE && (
                <ImageInput
                  label="Immagine di destinazione"
                  buttonText="Carica immagine"
                  setValue={onDestinationImagesChange}
                  value={destinationImages}
                  single
                  disabled={saving || loading}
                />
              )}
            </div>

            {formState.mode === MODES.SEAMLESS && (
              <div className={styles.splitFields}>
                <ImageInput
                  label="Immagine quadrata"
                  buttonText="Carica immagine"
                  setValue={setSeamlessImages}
                  value={seamlessImages}
                  single
                  disabled={saving || loading}
                />
                <TextInput
                  label="Titolo"
                  value={formState.seamlessTitle}
                  setValue={(value) => onChange("seamlessTitle", value)}
                />
                <TextInput
                  label="Testo"
                  value={formState.seamlessText}
                  setValue={(value) => onChange("seamlessText", value)}
                  multiline
                  rows={3}
                />
              </div>
            )}

            {formState.mode === MODES.BANNER && (
              <div className={styles.splitFields}>
                <ImageInput
                  label="Immagine (preferibilmente 868x118)"
                  buttonText="Carica immagine"
                  setValue={setBannerImages}
                  value={bannerImages}
                  single
                  disabled={saving || loading}
                />
              </div>
            )}

            <div className={styles.actions}>
              <Button
                text={formState.id ? "Aggiorna pubblicità" : "Crea pubblicità"}
                icon="save"
                onClick={onSave}
                disabled={saving || loading}
              />
              <Button
                text="Reset"
                variant="outlined"
                icon="restart_alt"
                onClick={resetForm}
                disabled={saving || loading}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className={styles.card}>
            <div className={styles.listHeader}>
              <span>Pubblicità salvate</span>
              <span className={styles.counter}>{advertisements.length}</span>
            </div>
            <div className={styles.tableHeader}>
              <span>Titolo/Utente</span>
              <span>Modalità</span>
              <span>Scadenza</span>
              <span>Categorie</span>
              <span>Destinazione</span>
              <span></span>
            </div>
            <div className={styles.listBody}>
              {advertisements.map((ad) => (
                <div className={styles.row} key={ad.id}>
                  <div>
                    <strong>{ad.title || "Sponsor"}</strong>
                    <p className={styles.secondary}>{ad.userId ? `Utente ${ad.userId}` : ""}</p>
                  </div>
                  <span className={styles.badge}>{ad.sponsorType === 2 ? "Seamless" : "Banner"}</span>
                  <span>{ad.endDate?.slice(0, 10)}</span>
                  <span className={styles.secondary}>
                    {(ad.sponsorCategories || [])
                      .map((item) => getCategoryDescriptionById(item.categoryId) || item.categoryId)
                      .join(", ")}
                  </span>
                  <span className={styles.secondary}>
                    {ad.sponsorDestinationType === 2 ? "Immagine" : ad.destinationUrl}
                  </span>
                  <div className={styles.actionsRow}>
                    <Button
                      size="mini"
                      icon="edit"
                      onClick={() => onEdit(ad)}
                      disabled={saving || loading}
                    />
                    <Button
                      size="mini"
                      variant="outlined"
                      icon="delete"
                      onClick={() => onDelete(ad.id)}
                      disabled={saving || loading}
                    />
                  </div>
                </div>
              ))}
              {advertisements.length === 0 && !loading && (
                <div className={styles.emptyState}>
                  <p>Nessuna pubblicità configurata. Crea il primo annuncio per iniziare.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPubblicita;
